<template>
  <transition name="fm-loader-fade">
    <div v-if="loading" class="fm-loader" :class="{ 'fm-loader--dim': dim }">

      <div class="fm-loader__card">

        <!-- Amber lightning badge -->
        <div class="fm-loader__badge">
          <svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
          </svg>
        </div>

        <h1 class="fm-loader__title">Flash Money</h1>
        <p class="fm-loader__sub">{{ message }}</p>

        <!-- Indeterminate progress bar -->
        <div class="fm-loader__track" role="progressbar" aria-label="Loading">
          <div class="fm-loader__fill"/>
        </div>

      </div>

      <!-- Subtle corner branding -->
      <p class="fm-loader__legal">Australian Credit Licence 508661</p>

    </div>
  </transition>
</template>

<script>
export default {
  name: 'LoadingPlaceholder',
  props: {
    loading: {
      type: Boolean,
      required: true,
    },
    message: {
      type: String,
      default: 'Setting up your application…',
    },
    dim: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style scoped lang="scss">
/* ── Full-screen overlay ─────────────────────────────────── */
.fm-loader {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #FBFAF6;

  /* Subtle warm grain texture via gradient */
  background-image:
    radial-gradient(ellipse 80% 60% at 50% -10%, rgba(255,176,0,0.08) 0%, transparent 70%);

  /* ── Dim/overlay variant (step transitions) ──────────── */
  &--dim {
    background: rgba(251, 250, 246, 0.88);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    background-image: none;
  }
}

/* ── Centre card ─────────────────────────────────────────── */
.fm-loader__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 24px;
  animation: fm-rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
}

/* ── Amber lightning badge ───────────────────────────────── */
.fm-loader__badge {
  width: 72px;
  height: 72px;
  border-radius: 22px;
  background: #FFB000;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  margin-bottom: 24px;
  box-shadow: 0 8px 28px rgba(255, 176, 0, 0.32), 0 2px 8px rgba(255, 176, 0, 0.18);
  animation: fm-pulse 2.8s ease-in-out infinite;
}

/* ── Typography ──────────────────────────────────────────── */
.fm-loader__title {
  font-size: 26px;
  font-weight: 800;
  color: #15171C;
  letter-spacing: -0.4px;
  margin: 0 0 8px;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, sans-serif;
}

.fm-loader__sub {
  font-size: 15px;
  color: #9197A1;
  margin: 0 0 32px;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, sans-serif;
}

/* ── Indeterminate progress bar ──────────────────────────── */
.fm-loader__track {
  width: 180px;
  height: 4px;
  background: rgba(255, 176, 0, 0.18);
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.fm-loader__fill {
  position: absolute;
  top: 0;
  left: -60%;
  width: 50%;
  height: 100%;
  background: linear-gradient(90deg, transparent, #FFB000, transparent);
  border-radius: 4px;
  animation: fm-slide 1.6s ease-in-out infinite;
}

/* ── Legal footnote ──────────────────────────────────────── */
.fm-loader__legal {
  position: absolute;
  bottom: 24px;
  font-size: 11px;
  color: #C8CACE;
  letter-spacing: 0.03em;
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, sans-serif;
}

/* ── Keyframes ───────────────────────────────────────────── */
@keyframes fm-rise {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes fm-pulse {
  0%, 100% { box-shadow: 0 8px 28px rgba(255,176,0,0.32), 0 2px 8px rgba(255,176,0,0.18); }
  50%       { box-shadow: 0 8px 36px rgba(255,176,0,0.50), 0 2px 12px rgba(255,176,0,0.28); }
}

@keyframes fm-slide {
  0%   { left: -60%; }
  100% { left: 120%; }
}

/* ── Mount/unmount transition ────────────────────────────── */
.fm-loader-fade-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}
.fm-loader-fade-leave-to {
  opacity: 0;
  transform: scale(1.015);
}
</style>
